/**
 * Conge (Leave) Types for HR Module
 */

// Leave types
export type TypeConge = 'CP' | 'CM' | 'CSS' | 'CMP' | 'EFM' | 'EFD';

// Leave request status
export type StatutConge = 'EN_ATTENTE' | 'VALIDEE' | 'REFUSEE' | 'JUSTIFIER';

// Employee info in conge response
export interface CongeEmployeeInfo {
  id: number;
  matricule: string;
  nom_complet: string;
  fonction: string | null;
  date_embauche: string;
}

// Validator info in conge response
export interface CongeValidatorInfo {
  id: number;
  email: string;
}

// Leave request
export interface Conge {
  id: number;
  ref_conge: string | null;  // Reference unique (format: CN26-001)
  employee_id: number;
  employee: CongeEmployeeInfo | null;
  type_conge: TypeConge;
  date_debut: string;
  date_fin: string;
  nbr_jours: number;
  motif: string | null;
  statut: StatutConge;
  validated_by: number | null;
  validator: CongeValidatorInfo | null;
  validated_at: string | null;
  commentaire_rh: string | null;
  date_demande: string;
  created_at: string;
  updated_at: string;
}

// Create leave request
export interface CongeCreate {
  employee_id: number;
  type_conge: TypeConge;
  date_debut: string;
  date_fin: string;
  motif?: string;
}

// Update leave request
export interface CongeUpdate {
  type_conge?: TypeConge;
  date_debut?: string;
  date_fin?: string;
  motif?: string;
  statut?: StatutConge;
  commentaire_rh?: string;
}

// Validate leave request
export interface CongeValidate {
  statut: 'VALIDEE' | 'REFUSEE' | 'JUSTIFIER';
  commentaire_rh?: string;
}

// Paginated list response
export interface CongeListResponse {
  items: Conge[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Leave balance
export interface SoldeConge {
  id: number;
  employee_id: number;
  annee: number;
  type_conge: TypeConge;
  jours_acquis: number;
  jours_pris: number;
  jours_reportes: number;
  jours_restants: number;
}

// Employee leave balance response
export interface EmployeeSoldeResponse {
  employee_id: number;
  employee: CongeEmployeeInfo;
  annee: number;
  soldes: SoldeConge[];
  total_jours_acquis: number;
  total_jours_pris: number;
  total_jours_restants: number;
}

// Recap item for annual summary
export interface RecapitulatifCongeItem {
  employee_id: number;
  matricule: string;
  nom_complet: string;
  fonction: string | null;
  cp_acquis: number;
  cp_pris: number;
  cp_restant: number;
  cm_pris: number;
  css_pris: number;
  autres_pris: number;
}

// Annual leave recap response
export interface RecapitulatifCongeResponse {
  annee: number;
  items: RecapitulatifCongeItem[];
  total_employees: number;
}

// Constants for dropdowns
export const TYPE_CONGE_OPTIONS: { value: TypeConge; label: string; description: string }[] = [
  { value: 'CP', label: 'CP - Congé Payé', description: 'Congé annuel payé (18 jours/an)' },
  { value: 'CM', label: 'CM - Congé Maladie', description: 'Congé pour raison médicale' },
  { value: 'CSS', label: 'CSS - Congé Sans Solde', description: 'Congé non rémunéré' },
  { value: 'CMP', label: 'CMP - Maternité/Paternité', description: 'Congé parental' },
  { value: 'EFM', label: 'EFM - Mariage', description: 'Événement familial - Mariage' },
  { value: 'EFD', label: 'EFD - Décès', description: 'Événement familial - Décès' },
];

export const STATUT_CONGE_OPTIONS: { value: StatutConge; label: string; color: string }[] = [
  { value: 'EN_ATTENTE', label: 'En attente', color: '#FFA726' },
  { value: 'VALIDEE', label: 'Validée', color: '#66BB6A' },
  { value: 'REFUSEE', label: 'Refusée', color: '#EF5350' },
  { value: 'JUSTIFIER', label: 'À justifier', color: '#42A5F5' },
];

// Helper functions
export const getTypeCongeLabel = (type: TypeConge): string => {
  const option = TYPE_CONGE_OPTIONS.find(o => o.value === type);
  return option ? option.label : type;
};

export const getStatutCongeLabel = (statut: StatutConge): string => {
  const option = STATUT_CONGE_OPTIONS.find(o => o.value === statut);
  return option ? option.label : statut;
};

export const getStatutCongeColor = (statut: StatutConge): string => {
  const option = STATUT_CONGE_OPTIONS.find(o => o.value === statut);
  return option ? option.color : '#9E9E9E';
};
